import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  createSyntec,
  getSyntecById,
  updateSyntecById,
  getSyntecList,
  deleteSyntecById,
  getSyntecCoefById,
  updateSyntecCoefById,
  createSyntecCoef,
  deleteSyntecCoefById,
  getSyntecCoefList,
  getSyntecCoefStaffById,
  updateSyntecCoefStaffById,
  createSyntecCoefStaff,
  deleteSyntecCoefStaffById,
  getSyntecCoefStaffList,
} from "./queries";

const SyntecContext = createContext({});

export const useSyntecContext = () => useContext(SyntecContext);

export const SyntecProvider = ({ ...props }) => {
  const [syntecList, setSyntecList] = useState([]);
  const [syntecPerId, setSyntecPerId] = useState({});
  const [syntecCoefById, setSyntecCoefById] = useState({});
  const [syntecCoefList, setSyntecCoefList] = useState([]);
  const [syntecCoefStaffById, setSyntecCoefStaffById] = useState({});
  const [syntecCoefStaffList, setSyntecCoefStaffList] = useState([]);

  const syntecCreate = useCallback(async (data) => {
    try {
      const syntec = await createSyntec(data);
      setSyntecList((old) => [...old, syntec]);
    } catch (e) {
      console.error("syntecCreate", e);
      throw e;
    }
  }, []);

  const syntecListGetter = useCallback(async () => {
    try {
      if (syntecList.length === 0) {
        const result = await getSyntecList();
        setSyntecList(result);
      }
    } catch (e) {
      console.error("syntecGetList", e);
    }
  }, [syntecList]);

  const syntecByIdGetter = useCallback(async (id) => {
    try {
      const result = await getSyntecById(id);
      setSyntecPerId(result);
    } catch (e) {
      console.error("syntecByIdGetter", e);
      throw e;
    }
  }, []);

  const syntecUpdateById = useCallback(async (id, data) => {
    try {
      const updatedSyntec = await updateSyntecById(id, data);
      setSyntecList((old) =>
        old.map((elm) => (elm.id === updatedSyntec.id ? updatedSyntec : elm))
      );
      setSyntecPerId(updatedSyntec);
    } catch (e) {
      console.error("positionUpdateById", e);
      throw e;
    }
  }, []);

  const syntecDeleteById = useCallback(async (id) => {
    try {
      await deleteSyntecById(id);
      setSyntecList((old) => old.filter((row) => row.id !== id));
    } catch (e) {
      console.error("syntecDeleteById", e);
      throw e;
    }
  }, []);

  const syntecCoefByIdGetter = useCallback(async (id) => {
    try {
      const result = await getSyntecCoefById(id);
      setSyntecCoefById(result);
    } catch (e) {
      console.error("syntecCoefByIdGetter", e);
      throw e;
    }
  }, []);

  const syntecCoefUpdateById = useCallback(async (id, data) => {
    try {
      if (data.syntec_id && !isNaN(data.syntec_id)) {
        data.syntec_id = parseInt(data.syntec_id, 10);
      }
      await updateSyntecCoefById(id, data);
      const updatedCoefList = await getSyntecCoefList();
      setSyntecCoefList(updatedCoefList);
    } catch (e) {
      console.error("syntecCoefUpdateById", e);
      throw e;
    }
  }, []);

  const syntecCoefCreate = useCallback(async (data) => {
    try {
      if (data.syntec_id && !isNaN(data.syntec_id)) {
        data.syntec_id = parseInt(data.syntec_id, 10);
      }
      await createSyntecCoef(data);
      const updatedCoefList = await getSyntecCoefList();
      setSyntecCoefList(updatedCoefList);
    } catch (e) {
      console.error("syntecCoefCreate", e);
      throw e;
    }
  }, []);

  const syntecCoefDeleteById = useCallback(async (id) => {
    try {
      await deleteSyntecCoefById(id);
      const updatedCoefList = await getSyntecCoefList();
      setSyntecCoefList(updatedCoefList);
    } catch (e) {
      console.error("syntecCoefDeleteById", e);
      throw e;
    }
  }, []);

  const syntecCoefListGetter = useCallback(async () => {
    try {
      if (syntecCoefList.length === 0) {
        const result = await getSyntecCoefList();
        setSyntecCoefList(result);
      }
    } catch (e) {
      console.error("syntecCoefListGetter", e);
      throw e;
    }
  }, [syntecCoefList]);

  const syntecCoefStaffByIdGetter = useCallback(async ({ id }) => {
    try {
      const result = await getSyntecCoefStaffById({
        id,
      });
      setSyntecCoefStaffById(result);
    } catch (e) {
      console.error("syntecCoefStaffByIdGetter", e);
      throw e;
    }
  }, []);

  const syntecCoefStaffUpdateById = useCallback(async ({ id, data }) => {
    try {
      if (data.syntec_id && !isNaN(data.syntec_id)) {
        data.syntec_id = parseInt(data.syntec_id, 10);
      }
      await updateSyntecCoefStaffById(id, data);
      const updatedCoefStaffList = await getSyntecCoefStaffList();
      setSyntecCoefStaffList(updatedCoefStaffList);
    } catch (e) {
      console.error("syntecCoefStaffUpdateById", e);
      throw e;
    }
  }, []);

  const syntecCoefStaffCreate = useCallback(async ({ data }) => {
    try {
      if (data.syntec_id && !isNaN(data.syntec_id)) {
        data.syntec_id = parseInt(data.syntec_id, 10);
      }
      await createSyntecCoefStaff(data);
      const updatedCoefStaffList = await getSyntecCoefStaffList();
      setSyntecCoefStaffList(updatedCoefStaffList);
    } catch (e) {
      console.error("syntecCoefStaffCreate", e);
      throw e;
    }
  }, []);

  const syntecCoefStaffDeleteById = useCallback(async ({ id }) => {
    try {
      await deleteSyntecCoefStaffById(id);
      const updatedCoefStaffList = await getSyntecCoefStaffList();
      setSyntecCoefStaffList(updatedCoefStaffList);
    } catch (e) {
      console.error("syntecCoefStaffDeleteById", e);
      throw e;
    }
  }, []);

  const syntecCoefStaffListGetter = useCallback(async () => {
    try {
      if (!syntecCoefStaffList || syntecCoefStaffList.length === 0) {
        const result = await getSyntecCoefStaffList();
        setSyntecCoefStaffList(result);
      }
    } catch (e) {
      console.error("syntecCoefStaffListGetter", e);
      throw e;
    }
  }, [syntecCoefStaffList]);

  const values = useMemo(
    () => ({
      syntecList,
      syntecCreate,
      syntecByIdGetter,
      syntecUpdateById,
      syntecListGetter,
      syntecDeleteById,
      syntecCoefByIdGetter,
      syntecCoefList,
      syntecCoefUpdateById,
      syntecCoefCreate,
      syntecCoefDeleteById,
      syntecCoefListGetter,
      syntecPerId,
      setSyntecPerId,
      setSyntecCoefById,
      syntecCoefById,
      setSyntecCoefStaffById,
      syntecCoefStaffById,
      syntecCoefStaffUpdateById,
      syntecCoefStaffCreate,
      syntecCoefStaffDeleteById,
      syntecCoefStaffByIdGetter,
      syntecCoefStaffListGetter,
      syntecCoefStaffList,
      setSyntecCoefStaffList,
    }),
    [
      syntecList,
      syntecCreate,
      syntecByIdGetter,
      syntecUpdateById,
      syntecListGetter,
      syntecDeleteById,
      syntecCoefByIdGetter,
      syntecCoefList,
      syntecCoefUpdateById,
      syntecCoefCreate,
      syntecCoefDeleteById,
      syntecCoefListGetter,
      syntecPerId,
      setSyntecPerId,
      setSyntecCoefById,
      syntecCoefById,
      setSyntecCoefStaffById,
      syntecCoefStaffById,
      syntecCoefStaffUpdateById,
      syntecCoefStaffCreate,
      syntecCoefStaffDeleteById,
      syntecCoefStaffByIdGetter,
      syntecCoefStaffListGetter,
      syntecCoefStaffList,
      setSyntecCoefStaffList,
    ]
  );
  return <SyntecContext.Provider value={values} {...props} />;
};
